import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "use-intl";

import {
  CalendarIcon,
  GlobeIcon,
  MusicIcon,
  SortAlphabeticallyIcon,
  StyleIcon,
} from "@/components/appIcons";
import {
  FacetFiltersClearButton,
  MobileFacetFiltersDisclosure,
} from "@/components/facets/facet-filter-controls";
import { FallbackImage } from "@/components/fallback-image";
import { Link } from "@/components/link";
import { FilterSelect, type FilterOption } from "@/components/ui/filter-select";
import { Spinner } from "@/components/ui/spinner";
import {
  getArtistDirectoryQueryOptions,
  type ArtistDirectoryIdentity,
  type ArtistDirectorySearchState,
} from "@/features/feature-page/artist-directory-query";
import { ArtistDirectoryDisclosure } from "@/features/feature-page/ArtistDirectoryDisclosure";
import { useTextInputFilter } from "@/hooks/use-text-input-filter";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";
import { formatCountry, getFlagUrl } from "@garimpa/lib/countries";
import { withImageRenderParams } from "@garimpa/lib/imageUrls";

type DirectoryData = RouterOutputs["featurePages"]["searchArtistDirectory"];
type DirectoryArtist = DirectoryData["artists"][number];
type DirectorySort = NonNullable<ArtistDirectorySearchState["sort"]>;
type UpdateFilter = (
  key: string,
  value: string | number | undefined,
  options?: { replace?: boolean },
) => void;
export type UpdateArtistDirectorySearch = (
  updater: (previous: ArtistDirectorySearchState) => ArtistDirectorySearchState,
  options?: { replace?: boolean },
) => void;

interface DirectoryFilterOptions {
  genres: FilterOption[];
  styles: FilterOption[];
  countries: FilterOption[];
  decades: FilterOption[];
  sorts: FilterOption[];
}

function defaultDirectorySort(search: ArtistDirectorySearchState): DirectorySort {
  return search.q ? "relevance" : "prominence";
}

function hasDirectoryFilters(search: ArtistDirectorySearchState, queryValue: string): boolean {
  return Boolean(
    queryValue.trim() ||
    search.genre ||
    search.style ||
    search.country ||
    search.decade ||
    search.sort,
  );
}

function activeFacetCount(search: ArtistDirectorySearchState): number {
  return [search.genre, search.style, search.country, search.decade].filter(Boolean).length;
}

function useGenreOptions(
  data: DirectoryData | undefined,
  family: "genre" | "style",
): FilterOption[] {
  const genres = useTranslations("shared.genres");
  return useMemo(
    () =>
      (data?.facets.genres ?? [])
        .filter((facet) => family !== "genre" || facet.value !== data?.subjectKey)
        .map((facet) => {
          const key = facet.value as Parameters<typeof genres>[0];
          return {
            value: facet.value,
            label: genres.has(key) ? genres(key) : formatCatalogTaxonomyLabel(facet.value),
            count: facet.count,
          };
        }),
    [data, family, genres],
  );
}

function useStyleOptions(
  data: DirectoryData | undefined,
  family: "genre" | "style",
): FilterOption[] {
  const styles = useTranslations("shared.styles");
  return useMemo(
    () =>
      (data?.facets.styles ?? [])
        .filter((facet) => family !== "style" || facet.value !== data?.subjectKey)
        .map((facet) => {
          const key = facet.value as Parameters<typeof styles>[0];
          return {
            value: facet.value,
            label: styles.has(key) ? styles(key) : formatCatalogTaxonomyLabel(facet.value),
            count: facet.count,
          };
        }),
    [data, family, styles],
  );
}

function useCountryOptions(data: DirectoryData | undefined): FilterOption[] {
  const locale = useLocale();
  return useMemo(
    () =>
      (data?.facets.country ?? []).map((facet) => ({
        value: facet.value,
        label: formatCountry(facet.value, locale, { includeUnknown: true }) ?? facet.value,
        count: facet.count,
        iconUrl: getFlagUrl(facet.value, 40) ?? undefined,
      })),
    [data, locale],
  );
}

function useDecadeOptions(data: DirectoryData | undefined): FilterOption[] {
  return useMemo(
    () =>
      (data?.facets.beginDecade ?? [])
        .map((facet) => ({
          value: facet.value,
          label: facet.value,
          count: facet.count,
        }))
        .sort((left, right) => Number(right.value) - Number(left.value)),
    [data],
  );
}

function useSortOptions(search: ArtistDirectorySearchState): FilterOption[] {
  const t = useTranslations("artistDirectory");
  return useMemo(() => {
    const options: FilterOption[] = [
      { value: "prominence", label: t("sortProminence") },
      { value: "name", label: t("sortName") },
      { value: "name-desc", label: t("sortNameDesc") },
      { value: "begin-asc", label: t("sortOldest") },
      { value: "begin-desc", label: t("sortNewest") },
    ];
    if (search.q) {
      return [{ value: "relevance", label: t("sortRelevance") }, ...options];
    }
    return options;
  }, [search.q, t]);
}

function DirectorySearchAndFilters({
  search,
  textSearch,
  options,
  controlsId,
  controlsOpen,
  onToggleControls,
  updateFilter,
}: {
  search: ArtistDirectorySearchState;
  textSearch: ReturnType<typeof useTextInputFilter>;
  options: DirectoryFilterOptions;
  controlsId: string;
  controlsOpen: boolean;
  onToggleControls: () => void;
  updateFilter: UpdateFilter;
}) {
  const t = useTranslations("artistDirectory");
  return (
    <div className="flex flex-col gap-3 border-b border-border pb-5 md:grid md:grid-cols-3 lg:grid-cols-6">
      <form
        role="search"
        aria-label={t("searchLabel")}
        className="w-full md:col-span-2"
        onSubmit={(event) => {
          event.preventDefault();
          textSearch.submit(textSearch.value);
        }}
      >
        <label className="block w-full">
          <span className="sr-only">{t("searchLabel")}</span>
          <input
            type="search"
            value={textSearch.value}
            onChange={(event) => textSearch.setValue(event.target.value)}
            placeholder={t("searchPlaceholder")}
            className="h-12 w-full rounded-md border border-input bg-background px-3"
          />
        </label>
      </form>
      <MobileFacetFiltersDisclosure
        activeCount={activeFacetCount(search)}
        controlsId={controlsId}
        open={controlsOpen}
        onToggle={onToggleControls}
      />
      <div
        id={controlsId}
        className={cn("w-full gap-3 sm:contents", controlsOpen ? "grid" : "hidden")}
      >
        <FilterSelect
          icon={MusicIcon}
          className="w-full"
          value={search.genre ?? null}
          onChange={(value) => updateFilter("genre", value ?? undefined)}
          options={options.genres}
          placeholder={t("allGenres")}
          searchPlaceholder={t("genreLabel")}
          searchable
          triggerClassName="h-12 min-h-12"
          variant="fullWidth"
        />
        <FilterSelect
          icon={StyleIcon}
          className="w-full"
          value={search.style ?? null}
          onChange={(value) => updateFilter("style", value ?? undefined)}
          options={options.styles}
          placeholder={t("allStyles")}
          searchPlaceholder={t("styleLabel")}
          searchable
          triggerClassName="h-12 min-h-12"
          variant="fullWidth"
        />
        <FilterSelect
          icon={GlobeIcon}
          className="w-full"
          value={search.country ?? null}
          onChange={(value) => updateFilter("country", value ?? undefined)}
          options={options.countries}
          placeholder={t("allCountries")}
          searchPlaceholder={t("countryLabel")}
          searchable
          triggerClassName="h-12 min-h-12"
          variant="fullWidth"
        />
        <FilterSelect
          icon={CalendarIcon}
          className="w-full"
          value={search.decade ? String(search.decade) : null}
          onChange={(value) => updateFilter("decade", value ? Number(value) : undefined)}
          options={options.decades}
          placeholder={t("allDecades")}
          searchPlaceholder={t("decadeLabel")}
          searchable
          triggerClassName="h-12 min-h-12"
          variant="fullWidth"
        />
      </div>
    </div>
  );
}

function DirectoryResultsToolbar({
  data,
  search,
  queryValue,
  sortOptions,
  onClear,
  updateFilter,
}: {
  data: DirectoryData | undefined;
  search: ArtistDirectorySearchState;
  queryValue: string;
  sortOptions: FilterOption[];
  onClear: () => void;
  updateFilter: UpdateFilter;
}) {
  const t = useTranslations("artistDirectory");
  const defaultSort = defaultDirectorySort(search);
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
        {t("results", { count: data?.totalArtists ?? 0 })}
      </p>
      <div className="flex items-center gap-2">
        {hasDirectoryFilters(search, queryValue) ? (
          <FacetFiltersClearButton onClick={onClear} label={t("clearFilters")} />
        ) : null}
        <FilterSelect
          icon={SortAlphabeticallyIcon}
          value={search.sort ?? defaultSort}
          onChange={(value) => {
            const sort = value as ArtistDirectorySearchState["sort"];
            updateFilter("sort", sort === defaultSort ? undefined : sort);
          }}
          options={sortOptions}
          placeholder={t("sortLabel")}
          clearable={false}
          intent="sort"
          triggerClassName="h-12 min-h-12"
        />
      </div>
    </div>
  );
}

function ArtistCard({ artist }: { artist: DirectoryArtist }) {
  return (
    <Link
      to="/artist/$id"
      params={{ id: artist.id }}
      className="block h-full rounded-xl border border-border bg-card p-3 transition hover:border-primary"
    >
      <div className="aspect-square overflow-hidden rounded-lg bg-secondary">
        <FallbackImage
          src={withImageRenderParams(artist.imageUrl, { width: 320 })}
          alt={artist.name}
          fallback="person"
          className="h-full w-full object-cover"
        />
      </div>
      <h3 className="mt-3 font-bold leading-tight">{artist.name}</h3>
      {artist.realname ? (
        <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{artist.realname}</p>
      ) : null}
    </Link>
  );
}

function ArtistDirectoryResults({
  data,
  isLoading,
  isError,
}: {
  data: DirectoryData | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const t = useTranslations("artistDirectory");
  if (isLoading) return <Spinner variant="page" />;
  if (isError) return <p className="py-12 text-center text-destructive">{t("error")}</p>;
  if (!data || data.artists.length === 0) {
    return <p className="py-12 text-center text-muted-foreground">{t("empty")}</p>;
  }
  return (
    <ul className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {data.artists.map((artist) => (
        <li key={artist.id}>
          <ArtistCard artist={artist} />
        </li>
      ))}
    </ul>
  );
}

function ArtistDirectoryPagination({
  data,
  onPageChange,
}: {
  data: DirectoryData | undefined;
  onPageChange: (page: number) => void;
}) {
  const t = useTranslations("artistDirectory");
  if (!data || data.totalPages <= 1) return null;
  return (
    <nav className="mt-8 flex items-center justify-center gap-4" aria-label={t("pagination")}>
      <button
        type="button"
        disabled={data.page <= 1}
        onClick={() => onPageChange(data.page - 1)}
        className="rounded-md border border-border px-4 py-2 disabled:opacity-40"
      >
        {t("previous")}
      </button>
      <span className="text-sm text-muted-foreground">
        {t("pageOf", { page: data.page, total: data.totalPages })}
      </span>
      <button
        type="button"
        disabled={data.page >= data.totalPages}
        onClick={() => onPageChange(data.page + 1)}
        className="rounded-md border border-border px-4 py-2 disabled:opacity-40"
      >
        {t("next")}
      </button>
    </nav>
  );
}

export function ArtistDirectory({
  family,
  subjectKey,
  heading,
  search,
  updateSearch,
}: {
  family: "genre" | "style";
  subjectKey: string;
  heading: string;
  search: ArtistDirectorySearchState;
  updateSearch: UpdateArtistDirectorySearch;
}) {
  const directory: ArtistDirectoryIdentity = { family, subjectKey };
  const query = useQuery(getArtistDirectoryQueryOptions(directory, search));
  const sectionRef = useRef<HTMLElement>(null);
  const pendingPageScroll = useRef<number | null>(null);
  const headingId = useId();
  const filterControlsId = useId();
  const [filterControlsOpen, setFilterControlsOpen] = useState(false);

  const updateFilter = useCallback<UpdateFilter>(
    (key, value, options) => {
      updateSearch((previous) => ({ ...previous, [key]: value, page: undefined }), options);
    },
    [updateSearch],
  );
  const textSearch = useTextInputFilter("q", search.q ?? "", updateFilter, 400);
  const shouldCollapseResults =
    query.data !== undefined &&
    query.data.artists.length > 6 &&
    !hasDirectoryFilters(search, textSearch.value) &&
    (search.page ?? 1) === 1;
  const filterOptions: DirectoryFilterOptions = {
    genres: useGenreOptions(query.data, family),
    styles: useStyleOptions(query.data, family),
    countries: useCountryOptions(query.data),
    decades: useDecadeOptions(query.data),
    sorts: useSortOptions(search),
  };

  const clearFilters = useCallback(() => {
    textSearch.reset();
    updateSearch(() => ({}));
  }, [textSearch, updateSearch]);

  const changePage = useCallback(
    (page: number) => {
      pendingPageScroll.current = page;
      updateSearch((previous) => ({
        ...previous,
        page: page > 1 ? page : undefined,
      }));
    },
    [updateSearch],
  );

  useEffect(() => {
    if (!query.data || pendingPageScroll.current !== query.data.page) return;
    pendingPageScroll.current = null;
    requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start",
      });
    });
  }, [query.data]);

  return (
    <section
      ref={sectionRef}
      className="mx-auto max-w-[1440px] scroll-mt-4 border-b border-border/40 px-4 pt-6 pb-10 md:px-10 md:pt-8 md:pb-14 lg:px-20"
      aria-labelledby={headingId}
    >
      <header className="mb-5 md:mb-6">
        <h2 id={headingId} className="text-2xl font-bold tracking-tight sm:text-3xl">
          {heading}
        </h2>
      </header>
      <DirectorySearchAndFilters
        search={search}
        textSearch={textSearch}
        options={filterOptions}
        controlsId={filterControlsId}
        controlsOpen={filterControlsOpen}
        onToggleControls={() => setFilterControlsOpen((current) => !current)}
        updateFilter={updateFilter}
      />
      <DirectoryResultsToolbar
        data={query.data}
        search={search}
        queryValue={textSearch.value}
        sortOptions={filterOptions.sorts}
        onClear={clearFilters}
        updateFilter={updateFilter}
      />
      <ArtistDirectoryDisclosure collapsible={shouldCollapseResults}>
        <ArtistDirectoryResults
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
        />
        <ArtistDirectoryPagination data={query.data} onPageChange={changePage} />
      </ArtistDirectoryDisclosure>
    </section>
  );
}
